import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { TopBar } from "../components/TopBar";
import { TabBar } from "../components/TabBar";
import { api } from "../api/client";
import { useExerciseLibrary } from "../hooks/useExerciseLibrary";
import type { TemplateExercise, WorkoutTemplate } from "../api/types";

export const TemplateEditor = () => {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { data: library } = useExerciseLibrary();
  const [tpl, setTpl] = useState<WorkoutTemplate | null>(null);
  const [picker, setPicker] = useState(false);
  const [pickerQ, setPickerQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<WorkoutTemplate>(`/templates/${id}`).then(setTpl).catch(() => {});
  }, [id]);

  const exMap = useMemo(() => new Map(library.map((e) => [e.id, e])), [library]);

  if (!tpl) return <div className="page center">Loading…</div>;

  const update = (patch: Partial<WorkoutTemplate>) => setTpl({ ...tpl, ...patch });

  const updateEx = (i: number, patch: Partial<TemplateExercise>) =>
    update({
      exercises: tpl.exercises.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    });

  const addEx = (exerciseId: string) => {
    update({
      exercises: [
        ...tpl.exercises,
        { exerciseId, order: tpl.exercises.length, targetSets: 3, targetReps: 10 },
      ],
    });
    setPicker(false);
  };

  const move = (i: number, dir: -1 | 1) => {
    const next = [...tpl.exercises];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    update({ exercises: next.map((e, idx) => ({ ...e, order: idx })) });
  };

  const removeEx = (i: number) =>
    update({
      exercises: tpl.exercises.filter((_, idx) => idx !== i).map((e, idx) => ({ ...e, order: idx })),
    });

  const save = async () => {
    setSaving(true);
    try {
      await api(`/templates/${id}`, {
        method: "PUT",
        body: { name: tpl.name, notes: tpl.notes, exercises: tpl.exercises },
      });
      nav(-1);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page">
        <TopBar title="Edit plan" back right={<button className="btn small" disabled={saving} onClick={() => void save()}>{saving ? "Saving…" : "Save"}</button>} />

        <label className="field">
          Name
          <input value={tpl.name} onChange={(e) => update({ name: e.target.value })} />
        </label>
        <label className="field" style={{ marginTop: 12 }}>
          Notes
          <textarea
            rows={2}
            value={tpl.notes ?? ""}
            onChange={(e) => update({ notes: e.target.value })}
          />
        </label>

        <h2 className="h2">Exercises</h2>
        <div className="col">
          {tpl.exercises.map((e, i) => {
            const lib = exMap.get(e.exerciseId);
            return (
              <div className="card" key={`${e.exerciseId}-${i}`}>
                <div className="row">
                  <strong>{lib?.name ?? e.exerciseId}</strong>
                  <div className="spacer" />
                  <button className="btn small secondary" onClick={() => move(i, -1)}>↑</button>
                  <button className="btn small secondary" onClick={() => move(i, 1)}>↓</button>
                  <button className="btn small secondary" onClick={() => removeEx(i)}>✕</button>
                </div>
                <div className="row" style={{ marginTop: 8, gap: 8 }}>
                  <label className="field" style={{ flex: 1 }}>
                    Sets
                    <input
                      type="number"
                      inputMode="numeric"
                      value={e.targetSets ?? ""}
                      onChange={(ev) => updateEx(i, { targetSets: Number(ev.target.value) || undefined })}
                    />
                  </label>
                  <label className="field" style={{ flex: 1 }}>
                    Reps
                    <input
                      type="number"
                      inputMode="numeric"
                      value={e.targetReps ?? ""}
                      onChange={(ev) => updateEx(i, { targetReps: Number(ev.target.value) || undefined })}
                    />
                  </label>
                  <label className="field" style={{ flex: 1 }}>
                    Weight
                    <input
                      type="number"
                      inputMode="decimal"
                      value={e.targetWeight ?? ""}
                      onChange={(ev) => updateEx(i, { targetWeight: Number(ev.target.value) || undefined })}
                    />
                  </label>
                </div>
                <label className="field" style={{ marginTop: 8 }}>
                  Notes
                  <input
                    value={e.notes ?? ""}
                    onChange={(ev) => updateEx(i, { notes: ev.target.value })}
                  />
                </label>
              </div>
            );
          })}
        </div>

        <button className="btn" style={{ marginTop: 12 }} onClick={() => setPicker(true)}>
          + Add exercise
        </button>
      </div>

      {picker && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 30,
            display: "flex",
            alignItems: "flex-end",
          }}
          onClick={() => setPicker(false)}
        >
          <div
            style={{
              background: "white",
              width: "100%",
              maxHeight: "80vh",
              overflowY: "auto",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              padding: 16,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="h2" style={{ marginTop: 0 }}>Add exercise</h2>
            <input
              autoFocus
              placeholder="Search…"
              value={pickerQ}
              onChange={(e) => setPickerQ(e.target.value)}
            />
            <div style={{ marginTop: 12 }}>
              {library
                .filter((e) =>
                  !pickerQ ||
                  e.name.toLowerCase().includes(pickerQ.toLowerCase()) ||
                  e.muscleGroup.includes(pickerQ.toLowerCase()),
                )
                .slice(0, 50)
                .map((e) => (
                  <button
                    key={e.id}
                    className="list-card"
                    onClick={() => addEx(e.id)}
                  >
                    <div className="thumb">{e.name.charAt(0)}</div>
                    <div>
                      <div style={{ fontWeight: 700 }}>{e.name}</div>
                      <div className="muted">{e.muscleGroup} · {e.equipment}</div>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
      <TabBar />
    </>
  );
};
